import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FiUpload, FiX, FiImage } from 'react-icons/fi';
import imageCompression from 'browser-image-compression';

const FileUpload = ({ onFileSelect, acceptedTypes = ['image/*'], maxSize = 10485760, multiple = false }) => {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [compressing, setCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    setError(null);
    setCompressing(false);
    setCompressionProgress(0);
    
    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.errors[0].code === 'file-too-large') {
        setError('檔案太大，請選擇 10MB 以下的檔案');
      } else if (rejection.errors[0].code === 'file-invalid-type') {
        setError('檔案類型不支援，請選擇圖片檔案');
      } else {
        setError('檔案上傳失敗');
      }
      return;
    }

    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      
      // 如果是圖片，進行壓縮
      if (file.type.startsWith('image/')) {
        try {
          // 檢查文件大小，如果已經小於 500KB，跳過壓縮
          const fileSizeMB = file.size / (1024 * 1024);
          
          if (fileSizeMB < 0.5) {
            // 文件已經小於 500KB，直接使用
            const reader = new FileReader();
            reader.onload = () => {
              setPreview(reader.result);
            };
            reader.readAsDataURL(file);
            onFileSelect(file);
          } else {
            // 文件大於 500KB，進行壓縮
            setCompressing(true);
            
            // 壓縮選項：目標大小 < 500KB
            const options = {
              maxSizeMB: 0.5,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
              onProgress: (progress) => {
                setCompressionProgress(Math.round(progress));
              }
            };
            
            const compressedFile = await imageCompression(file, options);
            
            setCompressing(false);
            setCompressionProgress(0);
            
            // 創建預覽
            const reader = new FileReader();
            reader.onload = () => {
              setPreview(reader.result);
            };
            reader.readAsDataURL(compressedFile);
            
            // 傳遞壓縮後的文件
            onFileSelect(compressedFile);
          }
        } catch (error) {
          console.error('圖片處理失敗:', error);
          setError('圖片處理失敗，請稍後再試');
          setCompressing(false);
          setCompressionProgress(0);
        }
      } else {
        // 非圖片文件，直接使用
        onFileSelect(file);
      }
    }
  }, [onFileSelect]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedTypes.reduce((acc, type) => {
      acc[type] = [];
      return acc;
    }, {}),
    maxSize,
    multiple
  });

  const removeFile = () => {
    setPreview(null);
    setError(null);
    onFileSelect(null);
  };

  return (
    <div className="space-y-4">
      {/* 上傳區域 */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        } ${error ? 'border-red-300 bg-red-50' : ''}`}
      >
        <input {...getInputProps()} />
        
        <div className="space-y-2">
          <FiUpload className="mx-auto h-8 w-8 text-gray-400" />
          
          {isDragActive ? (
            <p className="text-blue-600">放開以上傳檔案</p>
          ) : (
            <div>
              <p className="text-gray-600">
                拖拽檔案到這裡，或 <span className="text-blue-600">點擊選擇檔案</span>
              </p>
              <p className="text-sm text-gray-500 mt-1">
                支援 JPG, PNG, GIF, WebP 格式，最大 10MB（將自動壓縮至 500KB 以下）
              </p>
            </div>
          )}
        </div>
      </div>

      {/* 壓縮進度 */}
      {compressing && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <div className="flex items-center space-x-2">
            <div className="flex-1 bg-blue-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${compressionProgress}%` }}
              />
            </div>
            <span className="text-blue-600 text-sm font-medium">{compressionProgress}%</span>
          </div>
          <p className="text-blue-600 text-sm mt-2">正在壓縮圖片...</p>
        </div>
      )}

      {/* 錯誤訊息 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {/* 預覽 */}
      {preview && (
        <div className="relative">
          <div className="bg-gray-100 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <FiImage className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">預覽</span>
              </div>
              <button
                onClick={removeFile}
                className="p-1 text-gray-400 hover:text-red-600 rounded"
              >
                <FiX className="h-4 w-4" />
              </button>
            </div>
            <img
              src={preview}
              alt="預覽"
              className="max-w-full h-auto max-h-48 rounded"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload; 